import React, { useState, useEffect } from 'react';
import { isLink, Markdown, HexInput, SWATCHES } from './discordMarkdown';

// The dashboard's Embed tab (Components): Discord's newer message layout, built from blocks in any order:
// boxes (with a colour bar), text, sections (text with a thumbnail or button beside it), picture galleries, dividers
// and button rows. Discord's own limits apply: 40 components and 4,000 characters of text per message.

const DRAFT_KEY = 'dashboardComponentsDraft';
const MAX_COMPONENTS = 40;
const MAX_TEXT = 4000;

const NEW = {
  container: () => ({ type: 'container', color: '#5865f2', spoiler: false, children: [{ type: 'text', content: '' }] }),
  text: () => ({ type: 'text', content: '' }),
  section: () => ({ type: 'section', texts: [''], accessory: { type: 'thumbnail', url: '', description: '' } }),
  gallery: () => ({ type: 'gallery', items: [{ url: '', description: '', spoiler: false }] }),
  separator: () => ({ type: 'separator', divider: true, spacing: 'small' }),
  buttons: () => ({ type: 'buttons', buttons: [{ label: '', url: '', emoji: '' }] }),
};
const LABELS = { container: '📦 Box', text: '📝 Text', section: '🖼️ Section', gallery: '🎞️ Gallery', separator: '➖ Divider', buttons: '🔘 Buttons' };
const STARTER = [{
  type: 'container', color: '#fbe510', spoiler: false, children: [
    { type: 'text', content: '# Big announcement\nWrite anything here: **bold**, *italic*, [links](https://cheeks.uk), lists…' },
    { type: 'separator', divider: true, spacing: 'small' },
    { type: 'section', texts: ['**Live tonight at 7pm**\nOn Whatnot'], accessory: { type: 'button', label: 'Watch', url: 'https://www.whatnot.com/user/togecheeks' } },
  ],
}];

const loadDraft = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY));
    if (Array.isArray(saved)) return saved;
  } catch {}
  return STARTER;
};

// What Discord counts, as the bot does: every box, text, section, accessory, gallery, divider, row and button
const measure = (blocks) => {
  let components = 0, chars = 0;
  const one = (b) => {
    if (b.type === 'text' && b.content.trim()) { components += 1; chars += b.content.trim().length; }
    if (b.type === 'section') {
      const texts = b.texts.filter(t => t.trim());
      if (!texts.length) return;
      const hasAccessory = b.accessory.type === 'thumbnail' ? isLink(b.accessory.url) : (b.accessory.label || '').trim() && isLink(b.accessory.url);
      components += texts.length + (hasAccessory ? 2 : 0);
      chars += texts.reduce((n, t) => n + t.trim().length, 0);
    }
    if (b.type === 'gallery' && b.items.some(i => isLink(i.url))) components += 1;
    if (b.type === 'separator') components += 1;
    if (b.type === 'buttons') {
      const n = b.buttons.filter(x => ((x.label || '').trim() || x.emoji) && isLink(x.url)).length;
      if (n) components += 1 + n;
    }
    if (b.type === 'container') {
      const before = components;
      b.children.forEach(one);
      if (components > before) components += 1;
    }
  };
  blocks.forEach(one);
  return { components, chars };
};

// ── Preview ──────────────────────────────────────────────────────────
const LinkButton = ({ b }) => (
  <a href={isLink(b.url) ? b.url : undefined} target="_blank" rel="noreferrer"
    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded bg-[#4e5058] hover:bg-[#6d6f78] text-white text-sm font-medium">
    {b.emoji && <span>{b.emoji}</span>}{b.label}<span className="text-xs">↗</span>
  </a>
);

const PreviewBlock = ({ b, ctx }) => {
  if (b.type === 'text') return b.content.trim() ? <Markdown text={b.content} ctx={ctx} /> : null;
  if (b.type === 'section') {
    const texts = b.texts.filter(t => t.trim());
    if (!texts.length) return null;
    const a = b.accessory;
    const thumb = a.type === 'thumbnail' && isLink(a.url);
    const button = a.type === 'button' && (a.label || '').trim() && isLink(a.url);
    return (
      <div className="flex gap-3 items-start">
        <div className="flex-1 min-w-0 space-y-1">{texts.map((t, i) => <Markdown key={i} text={t} ctx={ctx} />)}</div>
        {thumb && <img src={a.url} alt="" className={`h-[85px] w-[85px] rounded-lg object-cover flex-shrink-0 ${a.spoiler ? 'blur-md' : ''}`} />}
        {button && <div className="flex-shrink-0 self-center"><LinkButton b={a} /></div>}
      </div>
    );
  }
  if (b.type === 'gallery') {
    const items = b.items.filter(i => isLink(i.url));
    if (!items.length) return null;
    const cols = items.length === 1 ? 'grid-cols-1' : items.length === 2 || items.length === 4 ? 'grid-cols-2' : 'grid-cols-3';
    return (
      <div className={`grid ${cols} gap-1 rounded-lg overflow-hidden max-w-[520px]`}>
        {items.map((i, n) => (
          <img key={n} src={i.url} alt={i.description || ''} title={i.description || ''}
            className={`w-full ${items.length === 1 ? 'max-h-80 object-contain bg-[#1e1f22]' : 'h-32 object-cover'} ${i.spoiler ? 'blur-md' : ''}`} />
        ))}
      </div>
    );
  }
  if (b.type === 'separator') {
    const space = b.spacing === 'large' ? 'my-3' : 'my-1';
    return b.divider ? <hr className={`border-[#3f4147] ${space}`} /> : <div className={b.spacing === 'large' ? 'h-6' : 'h-2'} />;
  }
  if (b.type === 'buttons') {
    const buttons = b.buttons.filter(x => ((x.label || '').trim() || x.emoji) && isLink(x.url));
    return buttons.length ? <div className="flex flex-wrap gap-2">{buttons.map((x, i) => <LinkButton key={i} b={x} />)}</div> : null;
  }
  if (b.type === 'container') {
    const children = b.children.map((c, i) => <PreviewBlock key={i} b={c} ctx={ctx} />).filter(Boolean);
    if (!children.length) return null;
    return (
      <div className={`rounded-lg bg-[#2b2d31] border border-[#3f4147] p-4 space-y-2 max-w-[560px] ${b.color ? 'border-l-4' : ''} ${b.spoiler ? 'blur-[2px]' : ''}`}
        style={b.color ? { borderLeftColor: b.color } : undefined}>
        {children}
      </div>
    );
  }
  return null;
};

const Preview = ({ blocks, bot, ctx }) => {
  const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const shown = blocks.map((b, i) => <PreviewBlock key={i} b={b} ctx={ctx} />).filter(Boolean);
  return (
    <div className="rounded-xl bg-[#313338] p-4 text-[#dbdee1] text-[15px] leading-[1.375rem]" style={{ fontFamily: 'gg sans, "Noto Sans", Helvetica, Arial, sans-serif' }}>
      <div className="flex gap-4">
        {bot?.avatar ? <img src={bot.avatar} alt="" className="h-10 w-10 rounded-full flex-shrink-0" /> : <div className="h-10 w-10 rounded-full bg-[#5865f2] flex-shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="font-medium text-white">{bot?.name || 'TOGΞ'}</span>
            <span className="text-[10px] font-semibold px-1 rounded bg-[#5865f2] text-white leading-4">APP</span>
            <span className="text-xs text-[#949ba4]">Today at {time}</span>
          </div>
          <div className="space-y-2">
            {shown.length ? shown : <p className="text-[#949ba4] italic text-sm">Your message will show here as you build it.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Editor ───────────────────────────────────────────────────────────
const inputClass = 'w-full rounded-md bg-zinc-950 border border-zinc-700 px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500';
const Small = ({ children, onClick, disabled, danger, title }) => (
  <button type="button" title={title} onClick={onClick} disabled={disabled}
    className={`px-1.5 py-0.5 rounded text-[11px] font-semibold disabled:opacity-30 ${danger ? 'text-red-400 hover:bg-red-900/30' : 'bg-zinc-800 hover:bg-zinc-700 text-white'}`}>
    {children}
  </button>
);
const LinkWarn = ({ url }) => (url && !isLink(url) ? <p className="text-amber-400 text-[10px]">Needs to be a full link starting https://</p> : null);

const AddMenu = ({ onAdd, inside }) => (
  <div className="flex flex-wrap gap-1">
    {Object.keys(NEW).filter(t => !(inside && t === 'container')).map(t => (
      <button key={t} type="button" onClick={() => onAdd(NEW[t]())}
        className="px-2 py-1 rounded-md border border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-[11px]">
        + {LABELS[t]}
      </button>
    ))}
  </div>
);

function BlockEditor({ block: b, set, inside }) {
  const patch = (changes) => set({ ...b, ...changes });
  if (b.type === 'text') {
    return <textarea value={b.content} rows={3} onChange={e => patch({ content: e.target.value })}
      placeholder="Markdown: # Heading, **bold**, *italic*, [link](https://…), - list, > quote, -# small" className={`${inputClass} resize-y`} />;
  }
  if (b.type === 'section') {
    const a = b.accessory;
    return (
      <div className="space-y-1.5">
        {b.texts.map((t, i) => (
          <div key={i} className="flex gap-1">
            <textarea value={t} rows={2} onChange={e => patch({ texts: b.texts.map((x, j) => (j === i ? e.target.value : x)) })}
              placeholder={`Text ${i + 1}`} className={`${inputClass} resize-y`} />
            {b.texts.length > 1 && <Small danger onClick={() => patch({ texts: b.texts.filter((_, j) => j !== i) })}>✕</Small>}
          </div>
        ))}
        {b.texts.length < 3 && <Small onClick={() => patch({ texts: [...b.texts, ''] })}>+ Text</Small>}
        <div className="flex gap-2 items-center flex-wrap pt-1">
          <span className="text-zinc-400 text-[11px]">Beside it:</span>
          {[['thumbnail', 'Thumbnail'], ['button', 'Link button']].map(([t, label]) => (
            <label key={t} className="flex items-center gap-1 text-[11px] text-zinc-300">
              <input type="radio" checked={a.type === t} onChange={() => patch({ accessory: t === 'thumbnail' ? { type: t, url: '', description: '' } : { type: t, label: '', url: '', emoji: '' } })} />{label}
            </label>
          ))}
        </div>
        {a.type === 'thumbnail' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            <input value={a.url} onChange={e => patch({ accessory: { ...a, url: e.target.value } })} placeholder="Picture link" className={inputClass} />
            <input value={a.description || ''} onChange={e => patch({ accessory: { ...a, description: e.target.value } })} placeholder="Description (optional)" className={inputClass} />
            <label className="flex items-center gap-1 text-[11px] text-zinc-400"><input type="checkbox" checked={!!a.spoiler} onChange={e => patch({ accessory: { ...a, spoiler: e.target.checked } })} /> Spoiler</label>
          </div>
        ) : (
          <div className="grid grid-cols-[60px_1fr_2fr] gap-1.5">
            <input value={a.emoji || ''} onChange={e => patch({ accessory: { ...a, emoji: e.target.value } })} placeholder="Emoji" className={inputClass} />
            <input value={a.label || ''} maxLength={80} onChange={e => patch({ accessory: { ...a, label: e.target.value } })} placeholder="Label" className={inputClass} />
            <input value={a.url} onChange={e => patch({ accessory: { ...a, url: e.target.value } })} placeholder="https://…" className={inputClass} />
          </div>
        )}
        <LinkWarn url={a.url} />
        <p className="text-zinc-600 text-[10px]">Without a picture or button, Discord shows a section as plain text.</p>
      </div>
    );
  }
  if (b.type === 'gallery') {
    return (
      <div className="space-y-1.5">
        {b.items.map((item, i) => (
          <div key={i} className="flex gap-1 items-center">
            <input value={item.url} onChange={e => patch({ items: b.items.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} placeholder="Picture link" className={inputClass} />
            <input value={item.description || ''} onChange={e => patch({ items: b.items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })} placeholder="Description" className={`${inputClass} !w-1/3`} />
            <label className="text-[10px] text-zinc-400 flex items-center gap-0.5" title="Spoiler"><input type="checkbox" checked={!!item.spoiler} onChange={e => patch({ items: b.items.map((x, j) => (j === i ? { ...x, spoiler: e.target.checked } : x)) })} />🙈</label>
            {b.items.length > 1 && <Small danger onClick={() => patch({ items: b.items.filter((_, j) => j !== i) })}>✕</Small>}
          </div>
        ))}
        {b.items.length < 10 && <Small onClick={() => patch({ items: [...b.items, { url: '', description: '', spoiler: false }] })}>+ Picture ({b.items.length}/10)</Small>}
      </div>
    );
  }
  if (b.type === 'separator') {
    return (
      <div className="flex gap-4 items-center flex-wrap">
        <label className="flex items-center gap-1 text-[11px] text-zinc-300"><input type="checkbox" checked={b.divider} onChange={e => patch({ divider: e.target.checked })} /> Show a line</label>
        {[['small', 'Small gap'], ['large', 'Big gap']].map(([s, label]) => (
          <label key={s} className="flex items-center gap-1 text-[11px] text-zinc-300"><input type="radio" checked={b.spacing === s} onChange={() => patch({ spacing: s })} />{label}</label>
        ))}
      </div>
    );
  }
  if (b.type === 'buttons') {
    return (
      <div className="space-y-1.5">
        {b.buttons.map((x, i) => (
          <div key={i} className="grid grid-cols-[60px_1fr_2fr_auto] gap-1.5">
            <input value={x.emoji || ''} onChange={e => patch({ buttons: b.buttons.map((y, j) => (j === i ? { ...y, emoji: e.target.value } : y)) })} placeholder="Emoji" className={inputClass} />
            <input value={x.label} maxLength={80} onChange={e => patch({ buttons: b.buttons.map((y, j) => (j === i ? { ...y, label: e.target.value } : y)) })} placeholder="Label" className={inputClass} />
            <input value={x.url} onChange={e => patch({ buttons: b.buttons.map((y, j) => (j === i ? { ...y, url: e.target.value } : y)) })} placeholder="https://…" className={inputClass} />
            <Small danger onClick={() => patch({ buttons: b.buttons.filter((_, j) => j !== i) })}>✕</Small>
          </div>
        ))}
        {b.buttons.length < 5 && <Small onClick={() => patch({ buttons: [...b.buttons, { label: '', url: '', emoji: '' }] })}>+ Button ({b.buttons.length}/5)</Small>}
      </div>
    );
  }
  if (b.type === 'container' && !inside) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-zinc-400 text-[11px]">Colour bar</span>
          <HexInput value={b.color} onChange={color => patch({ color })} allowEmpty swatches={SWATCHES.slice(0, 8)} />
          <label className="flex items-center gap-1 text-[11px] text-zinc-400"><input type="checkbox" checked={b.spoiler} onChange={e => patch({ spoiler: e.target.checked })} /> Spoiler (hidden until tapped)</label>
        </div>
        <BlockList blocks={b.children} setBlocks={children => patch({ children })} inside />
      </div>
    );
  }
  return null;
}

function BlockList({ blocks, setBlocks, inside }) {
  const setAt = (i, block) => setBlocks(blocks.map((x, j) => (j === i ? block : x)));
  const move = (i, by) => {
    const next = [...blocks];
    const [b] = next.splice(i, 1);
    next.splice(i + by, 0, b);
    setBlocks(next);
  };
  return (
    <div className={`space-y-2 ${inside ? 'pl-2 border-l-2 border-zinc-800' : ''}`}>
      {blocks.map((b, i) => (
        <div key={i} className={`rounded-lg border p-2 space-y-1.5 ${b.type === 'container' ? 'border-zinc-700 bg-zinc-900' : 'border-zinc-800 bg-zinc-950/50'}`}>
          <div className="flex items-center gap-1">
            <span className="text-zinc-300 text-[11px] font-semibold flex-1">{LABELS[b.type]}</span>
            <Small title="Move up" onClick={() => move(i, -1)} disabled={i === 0}>↑</Small>
            <Small title="Move down" onClick={() => move(i, 1)} disabled={i === blocks.length - 1}>↓</Small>
            <Small title="Duplicate" onClick={() => setBlocks([...blocks.slice(0, i + 1), JSON.parse(JSON.stringify(b)), ...blocks.slice(i + 1)])}>⧉</Small>
            <Small danger title="Remove" onClick={() => setBlocks(blocks.filter((_, j) => j !== i))}>✕</Small>
          </div>
          <BlockEditor block={b} set={block => setAt(i, block)} inside={inside} />
        </div>
      ))}
      <AddMenu onAdd={b => setBlocks([...blocks, b])} inside={inside} />
    </div>
  );
}

export default function ComponentsBuilder({ ask, lists, bot, channelOptions }) {
  const [blocks, setBlocks] = useState(loadDraft);
  const [channel, setChannel] = useState('');
  const [ping, setPing] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(blocks)); } catch {}
  }, [blocks]);

  const { components, chars } = measure(blocks);
  const tooBig = components > MAX_COMPONENTS || chars > MAX_TEXT;
  const ctx = { channels: lists.channels, roles: lists.roles };
  const allText = JSON.stringify(blocks);
  const pings = /@everyone|@here|<@&\d+>/.test(allText);

  const send = async () => {
    setSending(true); setResult(null);
    const answer = await ask('embed.send', { mode: 'components', channel, blocks, ping });
    setSending(false);
    if (answer.ok) setResult({ ok: true, text: answer.data.message, url: answer.data.url });
    else setResult({ ok: false, text: answer.error === 'timeout' ? "The bot didn't answer." : answer.error === 'login' ? 'Your login ran out: refresh and log in again.' : answer.error });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      <div className="space-y-3">
        <section className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-zinc-400 text-[11px] font-semibold uppercase tracking-wider">Blocks</h3>
            <span className={`text-[10px] ${tooBig ? 'text-red-400' : 'text-zinc-600'}`}>
              {components}/{MAX_COMPONENTS} components · {chars.toLocaleString()}/{MAX_TEXT.toLocaleString()} characters
            </span>
          </div>
          <BlockList blocks={blocks} setBlocks={setBlocks} />
        </section>
        <p className="text-zinc-600 text-[10px] px-1">
          Everything can go in any order, as many times as you like, up to Discord's own limits for one message: 40 components
          (every box, text, picture, divider and button counts) and 4,000 characters of text. Boxes hold the other blocks.
        </p>
      </div>

      <div className="space-y-3 lg:sticky lg:top-4">
        <Preview blocks={blocks} bot={bot} ctx={ctx} />
        <section className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-3 space-y-2.5">
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={channel} onChange={e => setChannel(e.target.value)}
              className="flex-1 rounded-md bg-zinc-950 border border-zinc-700 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-zinc-500">
              <option value="">Send to which channel?</option>
              {channelOptions()}
            </select>
            <button type="button" onClick={send} disabled={!channel || !components || sending || tooBig}
              className="px-4 py-1.5 rounded-md bg-[#5865f2] hover:bg-[#4752c4] text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <label className={`flex items-center gap-2 text-[11px] ${pings ? 'text-zinc-300' : 'text-zinc-600'}`}>
              <input type="checkbox" checked={ping} onChange={e => setPing(e.target.checked)} disabled={!pings} />
              Let @everyone, @here and role mentions ping people
            </label>
            <button type="button" onClick={() => { if (!confirmReset) { setConfirmReset(true); return; } setBlocks([]); setConfirmReset(false); setResult(null); }}
              onBlur={() => setConfirmReset(false)}
              className={`text-[11px] font-semibold px-2 py-1 rounded-md ${confirmReset ? 'bg-red-600 text-white' : 'text-zinc-500 hover:text-white'}`}>
              {confirmReset ? 'Clear everything?' : 'Start over'}
            </button>
          </div>
          {result && (
            <p className={`text-[11px] ${result.ok ? 'text-green-300' : 'text-red-300'}`}>
              {result.text}{' '}{result.url && <a href={result.url} target="_blank" rel="noreferrer" className="underline">Open it in Discord ↗</a>}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
